import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from './org/AppsModule';
import { apiGet, apiPost } from '../utils/backendApi';

// Unattended kit release (backend api/kitReleaseWorker.js): pin → canary →
// gate → prewarm → prod (perHost lanes) → twins → pool → done. One release
// at a time; the panel polls the active one every 5 s and shows every
// target as it moves. Cancel/pause/resume act between stamps.
const STAGES = ['pin', 'canary', 'gate', 'prewarm', 'prod', 'twins', 'pool', 'done'];
const fmtMs = (ms) => (ms == null ? '' : ms < 60e3 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms / 60e3)} min`);

// Fleet currency (GET /apps/admin/kit-currency): is kit main released, and
// is the fleet on it? Green main is tagged and rolled by the auto sweep
// after the quiet period; instances catch up in their maintenance window.
// "Release now (urgent)" does both at once — green is still required.
const fmtWhen = (ms) => (ms ? new Date(ms).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
function KitCurrency({ active, disabled, onReleased }) {
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const load = () => apiGet('/apps/admin/kit-currency').then((d) => { setC(d); setErr(null); }).catch((e) => setErr(e.message));
  useEffect(() => { load(); const t = setInterval(load, 60e3); return () => clearInterval(t); }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const releaseNow = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await apiPost('/apps/admin/kit-currency/release-now', {});
      setNote(r.kind === 'release' ? `tagged ${r.tag} and rolling it now (urgent)` : r.kind === 'reconcile' ? `catching ${r.due} instance(s) up to ${r.ref} now` : `nothing to do — ${r.main || ''}${r.fleet ? `; ${r.fleet}` : ''}`);
      if (r.id) onReleased?.(r.id);
      load();
    } catch (e) {
      setNote(`release now refused: ${e.message}`);
    } finally { setBusy(false); }
  };
  if (err) return <p className="cs-help">fleet currency: {err}</p>;
  if (!c) return <p className="cs-help">checking kit main and the fleet…</p>;
  const m = c.main, f = c.fleet;
  const due = f.behind.filter((b) => b.due), parked = f.behind.filter((b) => !b.due);
  const mainState = m.error ? { tone: 'warn', text: `kit main unreadable — ${m.error}` }
    : m.identical || !m.codeChanged ? { tone: 'ok', text: `kit main released${m.aheadBy ? ` (+${m.aheadBy} docs-only)` : ''}` }
      : m.check === 'success' ? { tone: c.stale.main ? 'bad' : 'warn', text: `kit main +${m.aheadBy} unreleased, green — ${c.active ? 'release running' : c.auto.enabled ? `auto-release after ${c.auto.quietMinutes} quiet min` : 'auto-release OFF'}` }
        : { tone: 'warn', text: `kit main +${m.aheadBy}, check ${m.check || 'pending'} — waits for green` };
  const fleetState = !f.ref ? { tone: 'warn', text: 'no fleet pin' }
    : f.behind.length === 0 ? { tone: 'ok', text: `fleet ${f.current}/${f.total} on ${f.ref}` }
      : { tone: c.stale.fleet || parked.some((b) => b.reason === 'failures') ? 'bad' : 'warn', text: `fleet ${f.current}/${f.total} on ${f.ref} · ${due.length} due${parked.length ? `, ${parked.length} by policy` : ''}` };
  const dot = (tone) => ({ ok: 'var(--ok)', warn: 'var(--warn)', bad: 'var(--danger)' }[tone]);
  const canRelease = !active && !m.error && ((m.codeChanged && m.check === 'success') || due.length > 0 || parked.some((b) => b.reason === 'window'));
  return (
    <div className="aa-confirm" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
      <span title={m.head ? `${m.head.sha?.slice(0, 7)} · ${m.head.title}` : ''}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: dot(mainState.tone), marginRight: 6 }} />{mainState.text}</span>
      <span title={f.behind.map((b) => `${b.slug}: ${b.from || '—'} → ${f.ref} (${b.due ? 'due' : b.reason}${b.until ? ` until ${fmtWhen(b.until)}` : ''})`).join('\n')}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: dot(fleetState.tone), marginRight: 6 }} />{fleetState.text}
      </span>
      {c.window ? <span className="cs-help">window {String(c.window.startHour).padStart(2, '0')}:00–{String(c.window.endHour).padStart(2, '0')}:00 {c.window.tz}</span> : null}
      {f.total ? (
        <span
          className="cs-help"
          title={[
            `${f.split} of ${f.total} instances update without downtime (blue/green app tier); the rest re-stamp in place`,
            ...(f.swapping || []).map((s) => `swapping now: ${s.slug} → ${s.color} for ${s.ref || '?'} since ${fmtWhen(s.since)}`),
            ...(f.gaps || []).slice().sort((a, b) => (b.at || 0) - (a.at || 0)).slice(0, 12).map((g) => `${g.slug}: edge gap ${g.gapMs ?? '?'} ms on ${g.ref || '?'} ${fmtWhen(g.at)}${g.flippedBack ? ' (FLIPPED BACK)' : ''}`),
          ].join('\n')}
        >
          {(f.gaps || []).some((g) => g.flippedBack) ? <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: dot('bad'), marginRight: 6 }} /> : null}
          {f.split}/{f.total} zero-downtime{f.swapping?.length ? ` · ${f.swapping.length} swapping` : ''}{f.gaps?.length ? ` · max gap ${Math.max(...f.gaps.map((g) => Number(g.gapMs) || 0))} ms` : ''}
        </span>
      ) : null}
      {c.auto.lastTag ? <span className="cs-help">last auto tag {c.auto.lastTag} {fmtWhen(c.auto.releasedAt)}</span> : null}
      <button type="button" className="cs-btn cs-btn-sm" disabled={disabled || busy || !canRelease} onClick={releaseNow} data-agent-guard="true" title="tag green main and roll it now, or catch the fleet up ignoring maintenance windows">Release now (urgent)</button>
      {note ? <span className="cs-help">{note}</span> : null}
    </div>
  );
}

function KitReleasePanel({ pinnedKitRef, busy: outerBusy }) {
  const [refDraft, setRefDraft] = useState('');
  const [scope, setScope] = useState('behind');
  const [exclude, setExclude] = useState('');
  const [urgent, setUrgent] = useState(false); // ignore maintenance windows
  const [force, setForce] = useState(false);   // roll held / auto-off instances too
  const [plan, setPlan] = useState(null);
  const [releases, setReleases] = useState(null);
  const [open, setOpen] = useState(null); // id of the expanded release
  const [detail, setDetail] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [word, setWord] = useState('');

  const load = async () => {
    try {
      const r = await apiGet('/apps/admin/kit-releases?limit=8');
      setReleases(r.releases || []);
    } catch (err) {
      setNotice(`kit releases: ${err.message}`);
    }
  };
  useEffect(() => { load(); }, []);
  const active = (releases || []).find((r) => ['queued', 'running', 'paused'].includes(r.status)) || null;
  const shown = open ? (releases || []).find((r) => r.id === open) || active : active || (releases || [])[0] || null;
  useEffect(() => {
    if (!active && !open) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [active?.id, open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!shown?.id) { setDetail(null); return undefined; }
    let gone = false;
    const pull = () => apiGet(`/apps/admin/kit-releases/${shown.id}?logs=40`).then((d) => { if (!gone) setDetail(d); }).catch(() => {});
    pull();
    const t = ['queued', 'running', 'paused'].includes(shown.status) ? setInterval(pull, 5000) : null;
    return () => { gone = true; if (t) clearInterval(t); };
  }, [shown?.id, shown?.status]);

  const dryRun = async () => {
    setBusy(true); setNotice(null);
    try {
      const r = await apiPost('/apps/admin/kit-releases', { ref: refDraft.trim(), scope, excludeSlugs: exclude, urgent, force, dryRun: true });
      setPlan(r.plan);
    } catch (err) {
      setNotice(`plan failed: ${err.message}`);
    } finally { setBusy(false); }
  };
  const start = async () => {
    setBusy(true); setNotice(null);
    try {
      const r = await apiPost('/apps/admin/kit-releases', { ref: refDraft.trim(), scope, excludeSlugs: exclude, urgent, force });
      setNotice(`kit release ${r.id} queued — ${r.plan.prod.length} prod + ${r.plan.twins.length} twins after the canary${r.plan.held?.length ? ` · ${r.plan.held.length} held/auto-off left out` : ''}`);
      setPlan(null); setWord(''); setRefDraft(''); setExclude(''); setOpen(r.id);
      await load();
    } catch (err) {
      setNotice(`release refused: ${err.message}`);
    } finally { setBusy(false); }
  };
  const control = async (id, action) => {
    setBusy(true); setNotice(null);
    try {
      await apiPost(`/apps/admin/kit-releases/${id}/${action}`, {});
      setNotice(`${action} requested — applies between stamps`);
      await load();
    } catch (err) {
      setNotice(`${action} failed: ${err.message}`);
    } finally { setBusy(false); }
  };

  const disabled = busy || outerBusy;
  const rel = detail?.release || shown;
  const stageState = (name) => (rel?.stages || []).find((s) => s.name === name);
  return (
    <div className="cs-card aa-rollout">
      <h3>Kit release <span className="cs-help">unattended: pin → canary → gate → prewarm → prod → twins → pool</span></h3>
      <KitCurrency active={active} disabled={disabled} onReleased={(id) => { setOpen(id); load(); }} />
      <h4 style={{ marginTop: 14 }}>Release a ref by hand</h4>
      <p className="cs-help">
        The canary (<span className="aa-mono">apps.kitRollout.canarySlugs</span>) is reprovisioned first and must phone home healthy at the new
        ref before anything else moves; every host then pulls the canary's image + SPA tarball so the fan-out never builds.
        A failed gate puts <span className="aa-mono">apps.kitRef</span> back. Pinned now: <span className="aa-mono">{pinnedKitRef || 'main'}</span>.
      </p>
      <div className="aa-env-actions">
        <input className="cs-input" value={refDraft} onChange={(e) => { setRefDraft(e.target.value); setPlan(null); }} placeholder="kit tag or branch (e.g. v2026.10.22)" aria-label="kit ref to release" disabled={disabled || !!active} />
        <label className="cs-help" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <select className="cs-input" value={scope} onChange={(e) => { setScope(e.target.value); setPlan(null); }} disabled={disabled || !!active} aria-label="scope">
            <option value="behind">instances behind the ref</option>
            <option value="all">every instance</option>
          </select>
        </label>
        <input className="cs-input" value={exclude} onChange={(e) => { setExclude(e.target.value); setPlan(null); }} placeholder="exclude slugs (known-bad, comma-separated)" aria-label="slugs to exclude" disabled={disabled || !!active} />
        <label className="cs-help" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title="stamp every instance as soon as the canary gate passes, ignoring maintenance windows">
          <input type="checkbox" checked={urgent} onChange={(e) => { setUrgent(e.target.checked); setPlan(null); }} disabled={disabled || !!active} /> urgent
        </label>
        <label className="cs-help" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title="also roll instances whose org turned auto-update off or put updates on hold">
          <input type="checkbox" checked={force} onChange={(e) => { setForce(e.target.checked); setPlan(null); }} disabled={disabled || !!active} /> force
        </label>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={disabled || !!active || !refDraft.trim()} onClick={dryRun}>Plan (dry run)</button>
        {active ? (
          <>
            {active.status === 'paused'
              ? <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={disabled} onClick={() => control(active.id, 'resume')}>Resume</button>
              : <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={disabled || active.status !== 'running'} onClick={() => control(active.id, 'pause')}>Pause</button>}
            <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={disabled} onClick={() => control(active.id, 'cancel')} data-agent-guard="true">Cancel release</button>
          </>
        ) : null}
      </div>
      {plan && !active ? (
        <div className="aa-confirm" style={{ marginTop: 10, flexWrap: 'wrap' }}>
          <span>
            <strong className="aa-mono">{plan.ref}</strong>: canary {plan.canaries.map((c) => (c.error ? `${c.slug} ✗ ${c.error}` : c.slug)).join(', ') || 'none'} ·{' '}
            <strong>{plan.prod.length}</strong> prod ({plan.prod.map((t) => t.slug).join(', ') || '—'}) · <strong>{plan.twins.length}</strong> twins ·{' '}
            {plan.pool.length} stale blank{plan.pool.length === 1 ? '' : 's'} · hosts {Object.entries(plan.perHost).map(([h, n]) => `${h}×${n}`).join(', ') || '—'} ·{' '}
            {plan.config.perHost}/host, verify {plan.config.verifyLite ? 'lite' : 'full'}{plan.held?.length ? ` · ${plan.held.length} left out (${plan.held.map((h) => `${h.slug}: ${h.reason}`).join(', ')})` : ''}{!urgent ? ' · out-of-window instances defer to their maintenance window' : ' · urgent: windows ignored'}. Type <strong>release</strong> to start:
          </span>
          <input className="cs-input" value={word} onChange={(e) => setWord(e.target.value)} placeholder="release" />
          <button type="button" className="cs-btn cs-btn-sm" disabled={disabled || word !== 'release' || plan.canaries.some((c) => c.error) || !plan.canaries.length} onClick={start} data-agent-guard="true">Release {plan.ref}</button>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => { setPlan(null); setWord(''); }}>Close</button>
        </div>
      ) : null}
      {notice ? <p className="cs-help" style={{ marginTop: 8 }}>{notice}</p> : null}

      {rel ? (
        <div style={{ marginTop: 12 }}>
          <div className="aa-twin-head" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="aa-mono">{rel.ref}</span>
            <StatusBadge status={rel.status} />
            <span className="cs-help">
              {rel.counts ? `${rel.counts.done}/${rel.counts.total} rolled${rel.counts.failed ? ` · ${rel.counts.failed} failed` : ''}${rel.counts.running ? ` · ${rel.counts.running} in flight` : ''}` : ''}
              {rel.durationMs ? ` · ${fmtMs(rel.durationMs)}` : rel.startedAt ? ` · ${fmtMs(Date.now() - rel.startedAt)} so far` : ''}
              {rel.requestedBy ? ` · by ${rel.requestedBy}` : ''}
              {rel.auto ? ' · automatic' : ''}{rel.reconcile ? ' · catch-up' : ''}{rel.urgent ? ' · urgent' : ''}{rel.force ? ' · force' : ''}
              {rel.deferred?.length ? ` · ${rel.deferred.length} deferred` : ''}
            </span>
            <span className="cs-help" title={rel.artifact ? `kit-src/${rel.ref}.tar.gz · sha256 ${rel.artifact.sha256 || ''}` : 'no source artifact in S3: hosts clone the kit from GitHub with the fleet token'} data-nova-note="Kit source artifact: whether this release's kit tree is published to S3 (hosts fetch it there, no GitHub call) or hosts fall back to a git clone">
              {rel.artifact ? `artifact ${fmtBytes(rel.artifact.bytes)}` : 'no artifact · git clone'}
            </span>
            {(releases || []).length > 1 ? (
              <select className="cs-input" value={rel.id} onChange={(e) => setOpen(e.target.value)} aria-label="release history" style={{ marginLeft: 'auto' }}>
                {(releases || []).map((r) => <option key={r.id} value={r.id}>{r.ref} · {r.status} · {new Date(r.createdAt).toLocaleString()}</option>)}
              </select>
            ) : null}
          </div>
          <div className="aa-env-actions" style={{ marginTop: 8 }}>
            {STAGES.map((name) => {
              const s = stageState(name);
              const cls = s?.status === 'done' ? 'is-up' : s?.status === 'running' ? 'is-warn' : s?.status ? 'is-down' : '';
              return <span key={name} className={`aa-health-badge ${cls}`} title={s?.detail || ''}>{name}{s?.ms ? ` ${fmtMs(s.ms)}` : ''}</span>;
            })}
          </div>
          {rel.error ? <p className="cs-help" style={{ color: 'var(--status-danger, #e05252)' }}>{rel.error}</p> : null}
          {rel.gate?.canaries?.length ? (
            <p className="cs-help">
              gate: {rel.gate.canaries.map((c) => `${c.slug} — kit ${c.kitRef || 'none'}, probe ${c.state || 'none'}, self-test ${c.selfTest ? (c.selfTest.ok ? `ok ${c.selfTest.passed}/${c.selfTest.total}` : `failing (${(c.selfTest.failing || []).map((f) => f.id).join(', ')})`) : 'none'}`).join(' · ')}
            </p>
          ) : null}
          {(rel.targets || []).length ? (
            <div className="aa-tablewrap" style={{ marginTop: 8 }}>
              <table className="aa-table">
                <thead><tr><th>Instance</th><th>Role</th><th>Host</th><th>From</th><th>Status</th><th>Time</th><th>Detail</th></tr></thead>
                <tbody>
                  {[...(rel.canary || []).map((c) => ({ ...c, role: 'canary' })), ...rel.targets].map((t) => (
                    <tr key={`${t.role}-${t.appId}`}>
                      <td className="aa-mono">{t.slug}</td>
                      <td>{t.role}</td>
                      <td className="aa-mono">{t.host || ''}</td>
                      <td className="aa-mono">{t.from || ''}</td>
                      <td><StatusBadge status={t.status === 'done' ? 'live' : t.status === 'error' ? 'error' : t.status === 'running' ? 'provisioning' : t.status} /></td>
                      <td>{fmtMs(t.ms)}</td>
                      <td className="cs-help">{t.error || (t.jobId ? `job ${t.jobId}` : '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {(rel.deferred || []).length ? (
            <p className="cs-help" style={{ marginTop: 8 }} title="deferred instances are stamped by the catch-up sweep when their window opens; held / auto-off instances wait for their org">
              deferred: {rel.deferred.map((d) => `${d.slug} (${d.reason}${d.until ? ` → ${new Date(d.until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''})`).join(' · ')}
            </p>
          ) : null}
          {(rel.commits || []).length ? (
            <details style={{ marginTop: 8 }}>
              <summary className="cs-help">commits ({rel.commits.length}){rel.commits.some((c) => c.bugs?.length || c.features?.length) ? ` · closes ${rel.commits.reduce((n, c) => n + (c.bugs?.length || 0) + (c.features?.length || 0), 0)} report(s)` : ''}</summary>
              <ul className="cs-help" style={{ margin: '4px 0 0 16px' }}>
                {rel.commits.map((c) => <li key={c.sha}><span className="aa-mono">{String(c.sha).slice(0, 7)}</span> {c.title}{c.bugs?.length ? ` · bug ${c.bugs.join(', ')}` : ''}{c.features?.length ? ` · feature ${c.features.join(', ')}` : ''}</li>)}
              </ul>
            </details>
          ) : null}
          {detail?.logs?.length ? (
            <details style={{ marginTop: 8 }}>
              <summary className="cs-help">log ({detail.logs.length})</summary>
              <pre className="aa-mono" style={{ fontSize: 12, maxHeight: 240, overflow: 'auto' }}>{detail.logs.map((l) => `${new Date(l.ts).toLocaleTimeString()}  ${l.line}`).join('\n')}</pre>
            </details>
          ) : null}
        </div>
      ) : releases && !releases.length ? <p className="cs-help" style={{ marginTop: 8 }}>No kit release yet.</p> : null}
    </div>
  );
}

// Fleet by org (fleet admin → Instances → "by org"): every prod instance
// beside its UAT twin, kit-ref drift against the pinned apps.kitRef, add-on
// chips, and the staged kit rollout — pin a ref, reprovision the UAT twins,
// verify, then reprovision prod. Each batch is a confirm-gated fan of the
// existing `reprovision` action (one provision job per instance).

function KitRefChip({ inst, pinned }) {
  const ref = inst.repo?.kitRef || '—';
  if (inst.kitTrack) {
    return <span className="aa-tag aa-tag-kit" title="kit-dev instance — follows this kit branch, skipped by releases">kit-dev → {inst.kitTrack}</span>;
  }
  const drift = pinned && ref !== '—' && ref !== pinned;
  return (
    <span className={`aa-tag aa-tag-kit ${drift ? 'is-drift' : ''}`} title={drift ? `pinned ${pinned}` : 'matches pinned kit ref'}>
      kit {ref}{inst.monitor?.versionBehind ? ' · behind' : ''}
    </span>
  );
}

function Twin({ inst, pinned, label }) {
  if (!inst) {
    return (
      <div className="aa-twin is-missing">
        <div className="aa-twin-head"><span className="aa-tag aa-tag-env">{label}</span><span className="cs-help">none</span></div>
      </div>
    );
  }
  return (
    <div className="aa-twin">
      <div className="aa-twin-head">
        <span className={`aa-tag aa-tag-env ${label === 'PROD' ? 'is-prod' : ''}`}>{label}</span>
        <span className="aa-mono">{inst.slug}</span>
        <StatusBadge status={inst.status} />
      </div>
      <div className="aa-twin-facts">
        <KitRefChip inst={inst} pinned={pinned} />
        {inst.version ? <span className="aa-tag">v{inst.version}</span> : null}
        {Object.entries(inst.template?.addons || {}).filter(([, v]) => v === true).map(([k]) => (
          <span key={k} className="aa-tag aa-tag-addon">+{k}</span>
        ))}
        {inst.monitor?.state ? <span className={`aa-health-badge is-${inst.monitor.state}`}>{inst.monitor.state}</span> : null}
      </div>
      {inst.urls?.app ? <a className="aa-twin-link" href={inst.urls.app} target="_blank" rel="noreferrer">{inst.urls.app.replace(/^https?:\/\//, '')}</a> : null}
    </div>
  );
}

const fmtBytes = (n) => (n == null ? '' : n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

export default function AdminFleetByOrg({ instances, gitShards, pinnedKitRef, busy, onReprovision, onPinKitRef }) {
  const [refDraft, setRefDraft] = useState('');
  const [confirm, setConfirm] = useState(null); // 'uat' | 'prod'
  const [word, setWord] = useState('');
  // Only instances that are not yet on the pinned ref (or report a kit
  // version behind apps.monitor.latestKitVersion) — the usual rollout.
  const [onlyBehind, setOnlyBehind] = useState(true);

  const groups = useMemo(() => {
    const byId = new Map((instances || []).map((i) => [i.appId, i]));
    const orgs = new Map();
    for (const i of instances || []) {
      if (i.envRole === 'uat') continue; // rendered beside its prod owner
      if (!orgs.has(i.orgId)) orgs.set(i.orgId, []);
      orgs.get(i.orgId).push({ prod: i, uat: i.uatAppId ? byId.get(i.uatAppId) || null : null });
    }
    // Orphan UAT instances (owner gone) still show up.
    for (const i of instances || []) {
      if (i.envRole !== 'uat') continue;
      const owned = (instances || []).some((p) => p.uatAppId === i.appId);
      if (owned) continue;
      if (!orgs.has(i.orgId)) orgs.set(i.orgId, []);
      orgs.get(i.orgId).push({ prod: null, uat: i });
    }
    return [...orgs.entries()].sort(([a], [b]) => String(a).localeCompare(String(b)));
  }, [instances]);

  // kit-dev instances (kitTrack) follow their own branch — never rolled.
  const managed = (list) => list.filter((i) => i && i.delivery !== 'external' && i.status !== 'terminated' && !i.kitTrack);
  const behind = (i) => (pinnedKitRef && i.repo?.kitRef && i.repo.kitRef !== pinnedKitRef) || i.monitor?.versionBehind === true;
  const narrow = (list) => (onlyBehind ? list.filter(behind) : list);
  const uatTargets = narrow(managed((instances || []).filter((i) => i.envRole === 'uat')));
  const prodTargets = narrow(managed((instances || []).filter((i) => i.envRole !== 'uat')));
  const drifted = (list) => list.filter(behind).length;

  const runBatch = async (which) => {
    const targets = which === 'uat' ? uatTargets : prodTargets;
    for (const inst of targets) {
      // eslint-disable-next-line no-await-in-loop
      await onReprovision(inst);
    }
    setConfirm(null);
    setWord('');
  };

  return (
    <>
      <KitReleasePanel pinnedKitRef={pinnedKitRef} busy={busy} />
      <div className="cs-card aa-rollout">
        <h3>Manual rollout <span className="cs-help">pinned apps.kitRef: <span className="aa-mono">{pinnedKitRef || 'main'}</span></span></h3>
        <p className="cs-help">
          The attended path when a release needs a hand: 1 · pin the kit ref every reprovision will check out → 2 · reprovision
          the UAT twins and verify them → 3 · reprovision prod. Each step fans out the existing <em>reprovision</em> action (one
          provision job per instance, claimed by the provisioner).
        </p>
        <div className="aa-env-actions">
          <input
            className="cs-input"
            value={refDraft}
            onChange={(e) => setRefDraft(e.target.value)}
            placeholder={pinnedKitRef || 'main'}
            aria-label="kit ref to pin"
            disabled={busy}
          />
          <button
            type="button"
            className="cs-btn cs-btn-sm cs-btn-outline"
            disabled={busy || !refDraft.trim() || refDraft.trim() === pinnedKitRef}
            onClick={async () => { await onPinKitRef(refDraft.trim()); setRefDraft(''); }}
          >
            Pin kit ref
          </button>
          <label className="cs-help" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={onlyBehind} onChange={(e) => setOnlyBehind(e.target.checked)} disabled={busy} />
            only instances behind the pin
          </label>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy || !uatTargets.length} onClick={() => { setConfirm('uat'); setWord(''); }}>
            Update {uatTargets.length} UAT twin{uatTargets.length === 1 ? '' : 's'}{!onlyBehind && drifted(uatTargets) ? ` (${drifted(uatTargets)} behind)` : ''}
          </button>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy || !prodTargets.length} onClick={() => { setConfirm('prod'); setWord(''); }}>
            Update {prodTargets.length} prod instance{prodTargets.length === 1 ? '' : 's'}{!onlyBehind && drifted(prodTargets) ? ` (${drifted(prodTargets)} behind)` : ''}
          </button>
        </div>
        {confirm && (
          <div className="aa-confirm" style={{ marginTop: 10 }}>
            <span>
              Reprovision <strong>{(confirm === 'uat' ? uatTargets : prodTargets).map((i) => i.slug).join(', ')}</strong> at kit ref{' '}
              <strong className="aa-mono">{pinnedKitRef || 'main'}</strong>
              {confirm === 'prod' ? ' — these are customer-facing.' : '.'} Type <strong>{confirm}</strong> to confirm:
            </span>
            <input className="cs-input" value={word} onChange={(e) => setWord(e.target.value)} placeholder={confirm} />
            <button type="button" className="cs-btn cs-btn-sm" disabled={busy || word !== confirm} onClick={() => runBatch(confirm)}>
              Reprovision
            </button>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => { setConfirm(null); setWord(''); }}>Cancel</button>
          </div>
        )}
      </div>

      {gitShards?.length ? (
        <p className="cs-help" data-nova-note="Git shards: the AWS accounts customer layer repositories are spread across, with repositories used against each account's CodeCommit quota; new organizations land on the least loaded one">
          Layer repos by shard:{' '}
          {gitShards.map((s) => <span key={s.id} className="aa-mono" style={{ marginRight: 10 }} title={`${s.accountId} · ${s.region}`}>{s.id} {s.repos}/{s.maxRepos}</span>)}
        </p>
      ) : null}
      {groups.map(([orgId, pairs]) => (
        <div key={orgId} className="cs-card aa-org-group">
          <h3>
            <Link to={`/orgs/${orgId}`}>{orgId}</Link>{' '}
            <span className="cs-help">{pairs.length} app{pairs.length === 1 ? '' : 's'}</span>
            {(() => { const sh = (pairs.find((p) => p.prod) || pairs[0]); const id = (sh.prod || sh.uat)?.gitShard; return id ? <span className="cs-help aa-mono" title="CodeCommit shard account this organization's layer repos live on" data-nova-note="Git shard this organization's layer repositories live on"> · shard {id}</span> : null; })()}
          </h3>
          {pairs.map(({ prod, uat }) => (
            <div key={(prod || uat).appId} className="aa-pair">
              <div className="aa-pair-name">
                {(prod || uat).name}
                {prod ? (
                  <Link className="cs-help" to={`/orgs/${orgId}/apps/${prod.appId}`}> detail</Link>
                ) : null}
              </div>
              <Twin inst={prod} pinned={pinnedKitRef} label="PROD" />
              <Twin inst={uat} pinned={pinnedKitRef} label="UAT" />
            </div>
          ))}
        </div>
      ))}
      {instances && !groups.length && <p className="cs-help">No instances yet.</p>}
    </>
  );
}
